/**
 * Single source of truth for the Partner Order From/To filter bounds (BDL20-3126).
 *
 * The order-list grid and the CSV export must filter over the SAME window. They used to build their
 * bounds independently — the grid seeded `to` as a bare date (so it excluded the final day until
 * "Apply" was pressed, which appended an end-of-day suffix), while the export interpolated whatever
 * it happened to hold, including raw `Date` objects. Everything now formats through this helper.
 *
 * Output shape is the one already stored in `partnerOrderVariables` and understood by the backend:
 *   lower bound -> `YYYY-MM-D`
 *   upper bound -> `YYYY-MM-D 23:55:55`
 * (month zero-padded, day not — kept as-is so existing "is the filter still default?" string
 * comparisons keep matching. The backend parses either form.)
 *
 * A `Date` is never returned or interpolated by callers: as a query-string value it renders as
 * "Sat Feb 01 2026 00:00:00 GMT+0530 (India Standard Time)", whose "+" decodes server-side to a
 * space and fails `DateTime` binding.
 */

/** `YYYY-M-D` or `YYYY-MM-DD`, optionally followed by a time component. */
const DATE_PREFIX_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})/

const END_OF_DAY_SUFFIX = ' 23:55:55'

export const formatPartnerOrderDateBound = (
  value: any,
  isUpperBound: boolean,
): string => {
  let year: number
  let month: number
  let day: number

  const match =
    typeof value === 'string' ? value.match(DATE_PREFIX_PATTERN) : null

  if (match) {
    /**
     * Read the calendar parts straight out of the string. Re-parsing with `new Date('2026-08-05')`
     * would treat it as UTC midnight and shift the day backwards for any viewer behind UTC.
     */
    year = Number(match[1])
    month = Number(match[2])
    day = Number(match[3])
  } else {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return ''
    }
    year = date.getFullYear()
    month = date.getMonth() + 1
    day = date.getDate()
  }

  const dateString = year + '-' + ('0' + month).slice(-2) + '-' + day

  return isUpperBound ? dateString + END_OF_DAY_SUFFIX : dateString
}

export default formatPartnerOrderDateBound
